import { useMemo } from 'react';
import { Route, Routes } from "react-router-dom";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as AppThemeProvider, useTheme } from './context/ThemeContext';
import { PrivacyProvider } from './context/PrivacyContext';
import Homepage from "./pages/homepage/Homepage";
import AccountsPage from "./pages/accounts/AccountsPage";
import EditAccountPage from "./pages/accounts/edit/EditAccountPage";
import CreateAccountPage from "./pages/accounts/create/CreateAccountPage";
import TransactionsPage from "./pages/transactions/TransactionsPage";
import EditTransactionPage from "./pages/transactions/edit/EditTransactionPage";
import ViewTransactionPage from "./pages/transactions/view/ViewTransactionPage";
import CreateTransactionPage from "./pages/transactions/create/CreateTransactionPage";
import CreateExpensePage from "./pages/transactions/expense/CreateExpensePage";
import ViewAccountPage from "./pages/accounts/view/ViewAccountPage";
import SubscriptionsPage from "./pages/subscriptions/SubscriptionsPage";
import CreateSubscriptionPage from "./pages/subscriptions/create/CreateSubscriptionPage";
import EditSubscriptionPage from "./pages/subscriptions/edit/EditSubscriptionPage";
import ViewSubscriptionPage from "./pages/subscriptions/view/ViewSubscriptionPage";
import Login from "./pages/auth/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Logout from "./pages/auth/Logout";
import RenewalsPage from "./pages/subscriptions/renewals/RenewalsPage";
import AppSubscriptionsPage from "./pages/subscriptions/appservices/AppSubscriptionsPage";
import InactiveSubscriptionsPage from "./pages/subscriptions/inactive/InactiveSubscriptionsPage";
import AnalysisPage from "./pages/analysis/AnalysisPage";
import LeaderboardPage from "./pages/leaderboard/LeaderboardPage";
import ActivityPage from "./pages/activity/ActivityPage";
import CalendarPage from "./pages/calendar/CalendarPage";
import CreateReferralPage from "./pages/referrals/create/CreateReferralPage";
import ReferralsPage from "./pages/referrals/ReferralsPage";
import ViewReferralPage from "./pages/referrals/view/ViewReferralPage";
import EditReferralPage from "./pages/referrals/edit/EditReferralPage";
import TrialFollowupsPage from "./pages/referrals/automated/TrialFollowupsPage";
import BotRequestsPage from "./pages/serverdata/requests/BotRequestsPage";
import IssuesPage from "./pages/serverdata/issues/IssuesPage";
import RemindersPage from "./pages/serverdata/reminders/RemindersPage";
import ChecklistPage from "./pages/serverdata/checklist/ChecklistPage";
import PointsPage from "./pages/points/PointsPage";
import AddPointsPage from "./pages/points/add/AddPointsPage";
import ViewPointsPage from "./pages/points/view/ViewPointsPage";
import AnnouncementsPage from "./pages/servermanagement/announcements/AnnouncementsPage";
import WalletsPage from './pages/wallets/WalletsPage';
import InvoicesPage from './pages/invoices/InvoicesPage';
import ResellersPage from "./pages/resellers/ResellersPage";
import CreateResellerCreditsPage from "./pages/resellers/credits/CreateResellerCreditsPage";
import ResellerRatesPage from "./pages/resellers/rates/ResellerRatesPage";
import './index.css';

const buildTheme = (dark) => createTheme({
  palette: {
    mode: dark ? 'dark' : 'light',
    primary:    { main: '#4F46E5', dark: '#4338CA', light: '#6366F1' },
    secondary:  { main: '#7C3AED' },
    background: dark
      ? { default: '#0b0c10', paper: '#13141a' }
      : { default: '#F8FAFC', paper: '#FFFFFF' },
    text: dark
      ? { primary: '#e2e8f0', secondary: '#94a3b8' }
      : { primary: '#0F172A', secondary: '#64748B' },
    success: { main: '#10B981' },
    error:   { main: '#EF4444' },
    warning: { main: '#F59E0B' },
    divider: dark ? '#1e2028' : '#E2E8F0',
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeightBold: 700,
  },
  shape: { borderRadius: 8 },
});

function AppRoutes() {
  const { dark } = useTheme();
  const muiTheme = useMemo(() => buildTheme(dark), [dark]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Homepage />} />
          <Route path="/analysis" element={<AnalysisPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/account/create" element={<CreateAccountPage />} />
          <Route path="/account/edit/:id" element={<EditAccountPage />} />
          <Route path="/account/view/:id" element={<ViewAccountPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/subscription/create" element={<CreateSubscriptionPage />} />
          <Route path="/subscription/inactive" element={<InactiveSubscriptionsPage />} />
          <Route path="/subscription/renewals" element={<RenewalsPage />} />
          <Route path="/appservices/subscriptions" element={<AppSubscriptionsPage />} />
          <Route path="/subscription/edit/:id" element={<EditSubscriptionPage />} />
          <Route path="/subscription/view/:id" element={<ViewSubscriptionPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/transaction/create" element={<CreateTransactionPage />} />
          <Route path="/transaction/expense/create" element={<CreateExpensePage />} />
          <Route path="/transaction/view/:id" element={<ViewTransactionPage />} />
          <Route path="/transaction/edit/:id" element={<EditTransactionPage />} />
          <Route path="/resellers" element={<ResellersPage />} />
          <Route path="/reseller/credits/create" element={<CreateResellerCreditsPage />} />
          <Route path="/reseller/rates" element={<ResellerRatesPage />} />
          <Route path="/referrals" element={<ReferralsPage />} />
          <Route path="/referrals/trial-followups" element={<TrialFollowupsPage />} />
          <Route path="/referral/view/:id" element={<ViewReferralPage />} />
          <Route path="/referral/edit/:id" element={<EditReferralPage />} />
          <Route path="/referral/create" element={<CreateReferralPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/serverdata/requests" element={<BotRequestsPage />} />
          <Route path="/serverdata/issues" element={<IssuesPage />} />
          <Route path="/serverdata/reminders" element={<RemindersPage />} />
          <Route path="/serverdata/checklist" element={<ChecklistPage />} />
          <Route path="/points" element={<PointsPage />} />
          <Route path="/wallets" element={<WalletsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/points/add" element={<AddPointsPage />} />
          <Route path="/points/view/:discordId" element={<ViewPointsPage />} />
          <Route path="/servermanagement/announcements" element={<AnnouncementsPage />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AppThemeProvider>
      <PrivacyProvider>
        <AppRoutes />
      </PrivacyProvider>
    </AppThemeProvider>
  );
}

export default App;
